// Angular
import {
	Component,
	OnDestroy,
	OnInit,
	HostListener,
	ComponentFactoryResolver
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
// State
import { Store } from '@ngxs/store';
import { UnregisterFromOrderChannel, SetOrderisRecalled } from './state/actions/quality.actions';
import { QualityState } from './state/quality.state';
// Model
import { USER_TIME_ACTIVITY } from './model/quality.constant';
import { OrderDetailModel } from './state/model/order-detail.model';
//eslint-disable-next-line
import { IDLE_USER_OPEN_MODAL, IDLE_USER_STILL_THERE } from '@cv-invision/cv.invision-views.ux';
import { RuleEngine } from './rules/model/rule-engine';
// Service
import { UserIdleService } from 'angular-user-idle';
import { TelemetryService } from '@cv-invision/cv.component-library.ux';
import {
	BroadcastService,
	TelemetryEventService,
	LoggerService,
	SubContentService
} from '@cv-invision/core';
// Other
import { Subscription, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
	template: `
		<cv-approve-modal></cv-approve-modal>
		<router-outlet></router-outlet>
		<cv-confirmation-inactivity></cv-confirmation-inactivity>
	`
})
export class QualityComponent extends RuleEngine implements OnInit, OnDestroy {
	private startDate: Date;
	private totalSeconds = 0;
	private orderDetail: OrderDetailModel;
	private pageName = 'Intelligence.Quality';
	private idleSeconds = USER_TIME_ACTIVITY.IDLE;
	private timeoutSeconds = USER_TIME_ACTIVITY.TIMEOUT;
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);

	private $timeStart: Subscription;
	private $timeOut: Subscription;
	private $order: Subscription;
	private $routerSub: Subscription;
	private $stillThere: any;

	constructor(
		public router: Router,
		public store: Store,
		private userIdle: UserIdleService,
		private telemetry: TelemetryService,
		public loggerService: LoggerService,
		private broadcastService: BroadcastService,
		public subContentService: SubContentService,
		public telemetryEventService: TelemetryEventService,
		public componentFactoryResolver: ComponentFactoryResolver
	) {
		super(componentFactoryResolver, router, store, loggerService, subContentService);
		this.$routerSub = router.events
			.pipe(filter(event => event instanceof NavigationEnd && event.url.includes('quality')))
			.subscribe((event:any) => {
				this.pageName = event.url.substring(1).replace(/\//g, '.');
			});
	}

	ngOnInit(): void {
		// Get all Rules when the main component opens
		this.getRules();

		const idleSeconds = parseInt(localStorage.getItem('idleSeconds'), 10);
		const timeoutSeconds = parseInt(localStorage.getItem('timeoutSeconds'), 10);

		this.idleSeconds =
			!isNaN(idleSeconds) && idleSeconds < USER_TIME_ACTIVITY.IDLE
				? idleSeconds
				: USER_TIME_ACTIVITY.IDLE;
		this.timeoutSeconds =
			!isNaN(timeoutSeconds) && timeoutSeconds < USER_TIME_ACTIVITY.TIMEOUT
				? timeoutSeconds
				: USER_TIME_ACTIVITY.TIMEOUT;

		this.$order = this.orderDetail$.subscribe((order: OrderDetailModel) => {
			if (order && this.orderDetail) {
				if (this.orderDetail.id !== order.id) {
					this.telemetryEventService.setEvent('review-stop', 'Order.Changed');
					this.unregisterUserFromInactivity();
				} else if (this.orderDetail.stateEnum !== order.stateEnum) {
					this.telemetryEventService.setEvent('review-stop', `Order.${order.stateEnum}`);
					this.unregisterUserFromInactivity();
				}
			}

			if (order) {
				if (
					!this.orderDetail ||
					this.orderDetail.id !== order.id ||
					this.orderDetail.stateEnum !== order.stateEnum
				) {
					this.orderDetail = order;
					setTimeout(() => this.watchIdleUser(), 100);

					this.broadcastService.broadcast({
						name: 'CHATBOT',
						event: {
							orderId: this.orderDetail.id
						}
					});
				}
			}
		});
	}

	/**
	 * Start tracking for the
	 * @param result: any
	 */
	watchIdleUser(): void {
		const { id: orderId, externalProvider, externalId, formType, stateEnum } = {
			...this.orderDetail
		};
		const payload = {
			orderId,
			externalProvider,
			externalId,
			formType,
			status: stateEnum
		};

		this.telemetry.trackEvent('review-start', payload, this.pageName);

		this.startDate = new Date();

		/**
		 * Idle -> seconds that the user has been inactive
		 * Timeout -> seconds after the idle has been fired
		 * Ping -> not necessary, we can add whatever value
		 */
		this.userIdle.setConfigValues({
			idle: this.idleSeconds,
			timeout: this.timeoutSeconds,
			ping: USER_TIME_ACTIVITY.PING
		});

		this.userIdle.startWatching();

		this.$timeStart = this.userIdle.onTimerStart().subscribe(count => {
			if (count === 1) {
				this.totalSeconds += (new Date().getTime() - this.startDate.getTime()) / 1000;
				this.totalSeconds -= this.idleSeconds;
				this.startDate = new Date();
				this.telemetry.trackEvent('review-inactive', payload, this.pageName);
				this.broadcastService.broadcast({ name: IDLE_USER_OPEN_MODAL, event: { isOpen: true } });
			}
		});

		this.$timeOut = this.userIdle.onTimeout().subscribe(() => {
			this.startDate = new Date();
			this.telemetry.trackEvent('review-timeout', payload, this.pageName);
			this.telemetryEventService.setEvent('review-stop', 'Inactivity.TimeOut');
			setTimeout(() => {
				this.router.navigate([`/intelligence/inbox`]);
			}, 100);
		});

		this.$stillThere = this.broadcastService.on(IDLE_USER_STILL_THERE, event => {
			this.startDate = new Date();
			this.telemetry.trackEvent('review-still-there', payload, this.pageName);
			this.userIdle.stopTimer();
		});
	}

	ngOnDestroy(): void {
		this.unregisterUserFromInactivity();
		this.unregisterUserFromOrderChannel();
		this.$order.unsubscribe();
		this.$routerSub.unsubscribe();
		// please don't mess with this line :(
		this.subContentService.close();
	}

	/**
	 * Catch the event when the user is leaving the page
	 */
	@HostListener('window:beforeunload', ['$event'])
	handleBeforeUnload(): void {
		this.telemetryEventService.setEvent('review-stop', 'Browser.Closed');
		this.unregisterUserFromInactivity();
		this.unregisterUserFromOrderChannel();
	}

	/**
	 * Unregister the user from the channel orders when the user leaves the page
	 */
	unregisterUserFromOrderChannel(): void {
		this.store.dispatch(new SetOrderisRecalled(null));
		this.store.dispatch(new UnregisterFromOrderChannel());
	}

	/**
	 * Unregister the user from inactivity watchers
	 */
	unregisterUserFromInactivity(): void {
		// Review stop, we need to send how much time the user was active
		if (this.orderDetail) {
			const { id: orderId, externalProvider, externalId, formType, stateEnum } = {
				...this.orderDetail
			};
			this.totalSeconds += (new Date().getTime() - this.startDate.getTime()) / 1000;
			const totalCount = Math.round(this.totalSeconds);

			if (totalCount > 0) {
				this.telemetry.trackEvent(
					'review-stop',
					{
						orderId,
						externalProvider,
						externalId,
						formType,
						activeTime: totalCount,
						status: stateEnum
					},
					this.pageName,
					this.telemetryEventService.getDescription('review-stop')
				);
			}
		}

		this.userIdle.stopWatching();
		if (this.$timeStart) {
			this.$timeStart.unsubscribe();
		}
		if (this.$timeOut) {
			this.$timeOut.unsubscribe();
		}
		if (this.$stillThere) {
			this.$stillThere();
		}
	}
}
